import { PropsWithChildren, ReactNode, forwardRef, useId } from 'react';
import {
  controlWrapper,
  disabled,
  errorBorder,
  errorText,
  input,
  inputWrapper,
  label,
  noBorder,
  notDisabled,
  supportingText,
  textDisabled,
} from './input.css';
import { Typography } from '../typography/typography';
import { clsx } from 'clsx';
import { sprinkles } from '@design-system/sprinkles.css';
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { anonymize } from '@client/new/pages/promotions/view-model/utils';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  hideError?: boolean;
  startIcon?: FontAwesomeIconProps;
  startCta?: ReactNode;
  endIcon?: FontAwesomeIconProps;
  label?: string;
  error?: string;
  supportingText?: string;
  testId?: string;
  endCta?: ReactNode;
  wrapperClassName?: string;
  inputWrapperClassName?: string;
  noBorder?: boolean;
};
const omitKeys: Array<keyof InputProps> = [
  'startIcon',
  'startCta',
  'endIcon',
  'supportingText',
  'inputWrapperClassName',
  'wrapperClassName',
  'hideError',
  'testId',
  'endCta',
  'noBorder',
];
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ hideError, name, testId, onBlur, type, ...p }, ref) => {
    const generatedInputId = useId().replaceAll(':', '');
    const inputId = p.id ?? `ds-input-${generatedInputId}`;
    const _onBlur: InputProps['onBlur'] = (e) => {
      if (onBlur) onBlur(e);
      if (testId || name)
        trackSessionEvent('InputBlur', {
          value: anonymize(`${p.value as string}`),
          name,
          testId,
        });
    };
    return (
      <div
        className={clsx(controlWrapper, p?.wrapperClassName)}
        data-testid={testId}
      >
        {p?.label ? (
          <InputLabel {...p} htmlFor={inputId}>
            {p.label}
          </InputLabel>
        ) : null}
        <div
          className={clsx(
            inputWrapper,
            p?.error && errorBorder,
            !p?.disabled && notDisabled,
            p?.disabled && disabled,
            p.inputWrapperClassName,
            p.noBorder && noBorder,
          )}
        >
          {p?.startCta ? p.startCta : null}
          {p?.startIcon ? (
            <FontAwesomeIcon data-testid="start-icon" {...p.startIcon} />
          ) : null}
          <input
            ref={ref}
            id={inputId}
            name={name ?? 'ds-input'}
            autoComplete="off"
            {..._.omit(p, omitKeys)}
            role="input"
            className={clsx(input)}
            onFocus={(e) => {
              e.target.select();
              p?.onFocus && p.onFocus(e);
              e.target.setSelectionRange(
                e.target.value.length,
                e.target.value.length,
              );
            }}
            {...(type === 'number'
              ? {
                  type: 'text',
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }
              : { type })}
            onBlur={_onBlur}
          />
          {p?.endIcon ? (
            <FontAwesomeIcon
              data-testid="end-icon"
              style={{ cursor: 'pointer' }}
              {...p.endIcon}
            />
          ) : null}
          {p?.endCta ? p.endCta : null}
        </div>
        {(p?.supportingText || p?.error) && !hideError ? (
          <Typography
            variant="body"
            size="small"
            className={clsx(supportingText, p?.error && errorText)}
          >
            {p.error || p.supportingText}
          </Typography>
        ) : null}
      </div>
    );
  },
);

export const InputLabel = (
  p: PropsWithChildren<
    Pick<InputProps, 'disabled' | 'error' | 'required'> & {
      htmlFor?: string;
    }
  >,
) => (
  <label htmlFor={p.htmlFor} className={sprinkles({ display: 'flex' })}>
    <Typography
      variant="body"
      size="medium"
      className={clsx(
        label,
        p?.error && errorText,
        p?.disabled && textDisabled,
      )}
    >
      {p.children}
    </Typography>
    {p?.required ? (
      <Typography
        variant="body"
        size="medium"
        className={clsx(
          errorText,
          sprinkles({ paddingTop: 's' }),
          p?.disabled && textDisabled,
        )}
      >
        *
      </Typography>
    ) : null}
  </label>
);
